import Link from 'next/link';
import { classes } from 'shared';

const ButtonSize = {
  xs: 'xs',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
} as const;

const ButtonType = {
  primary: 'primary',
  secondary: 'secondary',
  secondaryOutline: 'secondary-outline',
  secondaryWhite: 'secondary-white',
  primaryCarolina: 'primary-carolina',
  secondaryCarolina: 'secondary-carolina',
  tertiary: 'tertiary',
  quaternary: 'quaternary',
  danger: 'danger',
  dangerSecondary: 'danger-secondary',
  dangerTertiary: 'danger-tertiary',
  warning: 'warning',
  warningSecondary: 'warning-secondary',
  warningTertiary: 'warning-tertiary',
  cancel: 'cancel',
  square: 'square',
} as const;

export function Button({
  children,
  size = ButtonSize.md,
  bType = ButtonType.primary,
  icon,
  className,
  link,
  replace = false,
  loading,
  loadingText,
  complete,
  completeText,
  bRef,
  aRef,
  external,
  transparent,
  ...props
}: {
  children: React.ReactNode;
  size?: (typeof ButtonSize)[keyof typeof ButtonSize];
  icon?: React.ReactNode;
  className?: string;
  link?: string;
  replace?: typeof link extends string ? boolean : never;
  bType?: (typeof ButtonType)[keyof typeof ButtonType];
  loading?: boolean;
  loadingText?: string;
  complete?: boolean;
  completeText?: string;
  bRef?: React.Ref<HTMLButtonElement>;
  aRef?: React.Ref<HTMLAnchorElement>;
  external?: boolean;
  transparent?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>) {
  if (link && external) {
    return (
      <a {...props} href={link} target="_blank" rel="noopener noreferrer" className={calculateClasses()} ref={aRef}>
        {icon}
        {children}
      </a>
    );
  } else if (link) {
    return (
      <Link href={link} replace={replace} {...props} className={calculateClasses()} ref={aRef}>
        {icon}
        {children}
      </Link>
    );
  } else {
    return (
      <button
        {...props}
        type={props.type || 'button'}
        className={calculateClasses()}
        ref={bRef}
        disabled={props.disabled || loading}>
        {icon}
        {complete ? completeText : loading ? loadingText : children}
      </button>
    );
  }

  function calculateClasses() {
    switch (size) {
      case ButtonSize.xs:
        return classes(
          calculateColors(),
          className,
          'inline-flex items-center justify-center rounded-md border border-transparent px-2.5 py-1.5 text-xs font-medium',
          transparent && '!bg-transparent',
        );
      case ButtonSize.sm:
        return classes(
          calculateColors(),
          className,
          'inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium leading-4',
          transparent && '!bg-transparent',
        );
      case ButtonSize.lg:
        return classes(
          calculateColors(),
          className,
          'inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium',
          transparent && '!bg-transparent',
        );
      case ButtonSize.xl:
        return classes(
          calculateColors(),
          className,
          'inline-flex items-center justify-center rounded-md border border-transparent px-6 py-3 text-base font-medium',
          transparent && '!bg-transparent',
        );
      default:
        return classes(
          calculateColors(),
          className,
          'inline-flex items-center justify-center border border-transparent px-8 py-2 text-base font-medium md:px-12',
          bType !== ButtonType.square && 'rounded-lg',
          transparent && '!bg-transparent',
        );
    }
  }

  function calculateColors() {
    switch (bType) {
      case ButtonType.primary:
        return 'shadow-sm text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:hover:bg-blue-300';
      case ButtonType.secondary:
        return 'text-blue-500 !border-blue-500 !border-2 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:hover:bg-gray-100';
      case ButtonType.secondaryOutline:
        return 'text-blue-500 !border-blue-500 !border-2 hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:hover:bg-gray-100';
      case ButtonType.secondaryWhite:
        return 'text-white !border-white !border-2 hover:bg-grey-100 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:hover:bg-gray-100';
      case ButtonType.primaryCarolina:
        return 'shadow-sm text-white bg-blue-carolina hover:bg-blue-carolina/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-carolina disabled:bg-blue-300 disabled:hover:bg-blue-300';
      case ButtonType.secondaryCarolina:
        return 'text-blue-carolina !border-blue-carolina !border-2 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-carolina disabled:bg-gray-50 disabled:hover:bg-gray-100';
      case ButtonType.tertiary:
        return 'shadow-sm text-white bg-green-500 !border-2 !border-white hover:bg-green-600 focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-300 disabled:hover:bg-green-300';
      case ButtonType.quaternary:
        return 'text-green-500 bg-white !border-green-500 !border-2 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-green-100 disabled:hover:bg-green-100';
      case ButtonType.danger:
        return 'shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:hover:bg-red-300';
      case ButtonType.dangerSecondary:
        return 'text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-100 disabled:hover:bg-red-100';
      case ButtonType.dangerTertiary:
        return 'text-red-500 bg-white !border-red-500 !border-2 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-100 disabled:hover:bg-red-100';
      case ButtonType.warning:
        return 'shadow-sm text-white bg-yellow-600 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-600 disabled:bg-yellow-300 disabled:hover:bg-yellow-300';
      case ButtonType.warningSecondary:
        return 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-600 disabled:bg-yellow-100 disabled:hover:bg-yellow-100';
      case ButtonType.warningTertiary:
        return 'text-yellow-600 bg-white !border-yellow-600 !border-2 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-600 disabled:bg-yellow-100 disabled:hover:bg-yellow-100';
      case ButtonType.cancel:
        return 'bg-white !border !border-gray-300 shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';
      case ButtonType.square:
        return 'bg-white !border-2 !border-blue-500 font-medium text-blue-500 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500';
    }
  }
}
